import { Group, Stack } from "@mantine/core"
import { IconPlus } from "@tabler/icons-react"
import { keepPreviousData } from "@tanstack/react-query"
import { Button, Loader } from "@tfg/ui"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { SortBuilder } from "~/components/SortBuilder"
import { HeaderControlsProvider } from "~/components/contexts/useHeaderControlsContext"
import { FilterBuilderWrapper } from "~/components/table/FilterBuilderWrapper"
import { SearchBar } from "~/components/table/SearchBar"
import {
  FindProductionSitesForSupplierQueryResponse,
  useFindProductionSitesForSupplier,
} from "~/gen/api"
import { usePaginatedQuery } from "~/hooks/usePaginatedQuery"
import { EditProductionSiteDrawer } from "~/pages/SupplierPortal/ProductionSites/components/EditProductionSiteDrawer"
import { ProductionSiteTable } from "~/pages/SupplierPortal/ProductionSites/components/ProductionSiteTable"
import { useBuildProductionSiteFilters } from "~/pages/SupplierPortal/ProductionSites/hooks/useBuildProductionSiteFilters"
import { useBuildProductionSiteSorts } from "~/pages/SupplierPortal/ProductionSites/hooks/useBuildProductionSiteSorts"

export const SupplierProductionSites = () => {
  return (
    <HeaderControlsProvider>
      <SupplierProductionSitesContent />
    </HeaderControlsProvider>
  )
}

const SupplierProductionSitesContent = () => {
  const [modalOpened, setModalOpened] = useState(false)
  const { t } = useTranslation("supplier")

  const { filtersValue, setFiltersValue, title, searchPlaceholder, options } =
    useBuildProductionSiteFilters()

  const {
    sorts,
    setSorts,
    options: sortOptions,
  } = useBuildProductionSiteSorts()

  const {
    data: productionSitesData,
    isPending,
    isFetching,
    setPageBasedPagination,
    pageBasedPagination,
    totalPages,
  } = usePaginatedQuery<
    typeof useFindProductionSitesForSupplier,
    FindProductionSitesForSupplierQueryResponse
  >(
    useFindProductionSitesForSupplier,
    {
      filters: filtersValue,
      sorts,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    }
  )

  const productionSites = productionSitesData?.data ?? []
  const totalCount = productionSitesData?.count ?? 0

  return (
    <Stack>
      <Group justify="space-between">
        <Group justify="left">
          <SearchBar
            filtersValue={filtersValue}
            setFiltersValue={setFiltersValue}
            placeholder={t("productionSites.search.placeholder")}
            isFetching={isFetching}
          />
          <SortBuilder
            sorts={sorts}
            setSorts={setSorts}
            options={sortOptions}
          />
          <FilterBuilderWrapper
            title={title}
            searchPlaceholder={searchPlaceholder}
            options={options}
            setFilters={setFiltersValue}
            filters={filtersValue}
          />
        </Group>
        <Group>
          <Button icon={<IconPlus />} onClick={() => setModalOpened(true)}>
            {t("productionSites.buttons.add")}
          </Button>
        </Group>
      </Group>
      {isPending ? (
        <Loader fullPage />
      ) : (
        <ProductionSiteTable
          productionSites={productionSites}
          setPagination={setPageBasedPagination}
          pagination={pageBasedPagination}
          pageCount={totalPages}
          totalCount={totalCount}
        />
      )}
      <EditProductionSiteDrawer
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        onProductionSiteCreated={() => setModalOpened(false)}
        pagination={pageBasedPagination}
      />
    </Stack>
  )
}
